import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  DatePicker,
  Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const Unidades = () => {
  const { user } = useAuth();
  const [unidades, setUnidades] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUnidad, setEditingUnidad] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  
  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  useEffect(() => {
    fetchUnidades();
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);

  const fetchUnidades = async () => {
    setLoading(true);
    try {
      const response = await api.get('/unidades');
      setUnidades(response.data);
    } catch (error) {
      message.error('Error al cargar unidades');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Convertir fecha de dayjs a string
      const dataToSend = {
        ...values,
        fecha_ultimo_mantenimiento: values.fecha_ultimo_mantenimiento 
          ? values.fecha_ultimo_mantenimiento.format('YYYY-MM-DD')
          : null,
      };
      
      if (editingUnidad) {
        await api.put(`/unidades/${editingUnidad.id}`, dataToSend);
        message.success('Unidad actualizada exitosamente');
      } else {
        await api.post('/unidades', dataToSend);
        message.success('Unidad creada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUnidad(null);
      fetchUnidades();
    } catch (error) {
      message.error('Error al guardar unidad');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingUnidad(record);
    form.setFieldsValue({
      ...record,
      empresa_id: record.empresa_id || undefined,
      fecha_ultimo_mantenimiento: record.fecha_ultimo_mantenimiento 
        ? dayjs(record.fecha_ultimo_mantenimiento) 
        : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/unidades/${id}`);
      message.success('Unidad eliminada exitosamente');
      fetchUnidades();
    } catch (error) {
      message.error('Error al eliminar unidad');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Tipo de Grúa',
      dataIndex: 'tipo_grua',
      key: 'tipo_grua',
    },
    {
      title: 'Capacidad',
      dataIndex: 'capacidad',
      key: 'capacidad',
      render: (capacidad) => `${capacidad} ton`,
    },
    {
      title: 'Marca',
      dataIndex: 'marca',
      key: 'marca',
    },
    {
      title: 'Modelo',
      dataIndex: 'modelo',
      key: 'modelo',
    },
    {
      title: 'Año',
      dataIndex: 'anio',
      key: 'anio',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa',
      render: (empresa) => empresa || <Tag color="default">Sin empresa</Tag>,
    }] : []),
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          disponible: 'green',
          en_servicio: 'blue',
          mantenimiento: 'orange',
          inactiva: 'red',
        };
        return <Tag color={colors[estado]}>{estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Kilometraje Actual',
      dataIndex: 'km_actual',
      key: 'km_actual',
      render: (km) => km ? `${parseFloat(km).toFixed(2)} km` : '0.00 km',
    },
    {
      title: 'Próximo Mantenimiento',
      key: 'mantenimiento',
      render: (_, record) => {
        const mantenimientos = [];
        
        // Mantenimiento por fecha
        if (record.proximo_mantenimiento) {
          const diasRestantes = record.dias_restantes_mantenimiento;
          const fecha = dayjs(record.proximo_mantenimiento).format('DD/MM/YYYY');
          if (diasRestantes !== null && diasRestantes >= 0 && diasRestantes <= 5) {
            mantenimientos.push(
              <Tag key="fecha" color={diasRestantes <= 3 ? 'red' : 'orange'}>
                Fecha: {fecha} ({diasRestantes} días)
              </Tag>
            );
          } else {
            mantenimientos.push(
              <Tag key="fecha" color="default">
                Fecha: {fecha}
              </Tag>
            );
          }
        }
        
        // Mantenimiento por kilometraje
        if (record.km_proximo_mantenimiento && record.km_actual !== null) {
          const kmRestantes = parseFloat(record.km_proximo_mantenimiento) - parseFloat(record.km_actual);
          const kmProximo = parseFloat(record.km_proximo_mantenimiento).toFixed(2);
          if (kmRestantes >= 0 && kmRestantes <= 100) {
            mantenimientos.push(
              <Tag key="km" color={kmRestantes <= 50 ? 'red' : 'orange'}>
                Km: {kmProximo} km (faltan {kmRestantes.toFixed(2)} km)
              </Tag>
            );
          } else {
            mantenimientos.push(
              <Tag key="km" color="default">
                Km: {kmProximo} km
              </Tag>
            );
          }
        }
        
        return mantenimientos.length > 0 ? <Space direction="vertical" size="small">{mantenimientos}</Space> : '-';
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta unidad?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Unidades/Grúas"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUnidad(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nueva Unidad
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={unidades}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingUnidad ? 'Editar Unidad' : 'Nueva Unidad'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingUnidad(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="placa"
            label="Placa"
            rules={[{ required: true, message: 'Ingresa la placa' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="tipo_grua"
            label="Tipo de Grúa"
            rules={[{ required: true, message: 'Ingresa el tipo de grúa' }]}
          >
            <Select placeholder="Selecciona el tipo de grúa">
              <Select.Option value="Grúa Plataforma">Grúa Plataforma</Select.Option>
              <Select.Option value="Grúa Pesada">Grúa Pesada</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="capacidad"
            label="Capacidad (toneladas)"
            rules={[{ required: true, message: 'Ingresa la capacidad' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="marca" label="Marca">
            <Input />
          </Form.Item>
          <Form.Item name="modelo" label="Modelo">
            <Input />
          </Form.Item>
          <Form.Item name="anio" label="Año">
            <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="numero_serie" label="Número de Serie">
            <Input />
          </Form.Item>
          {isMainAdmin && (
            <Form.Item name="empresa_id" label="Empresa">
              <Select placeholder="Selecciona una empresa" allowClear>
                {empresas.map((empresa) => (
                  <Select.Option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="estado" label="Estado" initialValue="disponible">
            <Select>
              <Select.Option value="disponible">Disponible</Select.Option>
              <Select.Option value="en_servicio">En Servicio</Select.Option>
              <Select.Option value="mantenimiento">Mantenimiento</Select.Option>
              <Select.Option value="inactiva">Inactiva</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item 
            name="km_actual" 
            label="Kilometraje Actual"
            initialValue={0}
          >
            <InputNumber 
              min={0} 
              precision={2} 
              style={{ width: '100%' }} 
              placeholder="Ingresa el kilometraje actual de la unidad"
            />
          </Form.Item>
          <Form.Item name="fecha_ultimo_mantenimiento" label="Fecha Último Mantenimiento">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="dias_mantenimiento" label="Días entre Mantenimientos">
            <InputNumber 
              min={1} 
              style={{ width: '100%' }} 
              placeholder="Ej: 30, 60, 90 días"
              tooltip="Cantidad de días que deben transcurrir entre mantenimientos"
            />
          </Form.Item>
          
          <Divider>Mantenimiento por Kilometraje</Divider>
          
          <Form.Item 
            name="km_ultimo_mantenimiento" 
            label="Kilometraje del Último Mantenimiento"
            tooltip="Kilometraje al que se realizó el último mantenimiento"
          >
            <InputNumber 
              min={0} 
              precision={2} 
              style={{ width: '100%' }} 
              placeholder="Ej: 50000 km"
            />
          </Form.Item>
          
          <Form.Item 
            name="km_intervalo_mantenimiento" 
            label="Intervalo de Kilometraje para Mantenimiento"
            tooltip="Cantidad de kilómetros que deben transcurrir entre mantenimientos"
          >
            <InputNumber 
              min={1} 
              precision={2} 
              style={{ width: '100%' }} 
              placeholder="Ej: 10000 km"
            />
          </Form.Item>
          
          <Form.Item shouldUpdate={(prevValues, currentValues) => 
            prevValues.km_ultimo_mantenimiento !== currentValues.km_ultimo_mantenimiento ||
            prevValues.km_intervalo_mantenimiento !== currentValues.km_intervalo_mantenimiento
          }>
            {() => {
              const kmUltimo = form.getFieldValue('km_ultimo_mantenimiento');
              const kmIntervalo = form.getFieldValue('km_intervalo_mantenimiento');
              const kmProximo = (kmUltimo && kmIntervalo) ? parseFloat(kmUltimo) + parseFloat(kmIntervalo) : null;
              
              return kmProximo && !isNaN(kmProximo) ? (
                <Form.Item label="Próximo Mantenimiento por Kilometraje">
                  <Input 
                    value={`${kmProximo.toFixed(2)} km`} 
                    disabled 
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                {editingUnidad ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingUnidad(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Unidades;

